import type { ReactElement } from 'react';
import type React from 'react';
import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { IconButton } from '../../../../components/Button/IconButton';
import { GrandAccessBanner } from '../../../../components/GrandAccessBanner';
import { Modal } from '../../../../components/Modal';
import type { Company } from '../../../../domain/company';
import type { Signal } from '../../../../domain/signal';
import { SignalType } from '../../../../domain/signal';
import { useUserStatus } from '../../../../hooks/useUserStatus';
import { AssetsService } from '../../../../services/AssetsService';
import { useDispatchTrackEventWithLocation } from '../../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { ContactUsSuccessModal } from '../../../settings/ContactUsSuccessModal/ContactUsSuccessModal';

import { AdNetworkSignal } from './components/AdNetworkSignal/AdNetworkSignal';
import { IntentSignal } from './components/IntentSignal/IntentSignal';
import { NewsSignal } from './components/NewsSignal/NewsSignal';
import { TechnologySignal } from './components/TechnologySignal/TechnologySignal';
import { TrafficSignal } from './components/TrafficSignal/TrafficSignal';
import { StyledAllSignalsButton, StyledSignalsContent } from './styles';

type Props = {
  data: Signal[];
  company: Company;
};

const ICON_IMAGE_PATH = AssetsService.assetUrl('/images/hot_locked.svg');
const DEFAULT_BANNER_POSITION = 1;

export const SignalsContent: React.FC<Props> = ({ data, company }) => {
  const { t } = useTranslation();
  const { isTrialOrAfterTrial, isNoTouch } = useUserStatus();
  const isShowIntentBanner = isTrialOrAfterTrial || isNoTouch;
  const [isHookModalOpen, setIsHookModalOpen] = useState(false);

  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const handleAllSignalsClick = () => {
    dispatchTrackEventWithLocation('signals', 'open signals in platform', 'see all signals');
    window.open(`https://pro.similarweb.com/#/sales/account-overview/${company.domain}/signals`, '_blank');
  };

  const renderSignals = (signal: Signal, index: number): ReactElement | null => {
    switch (signal.type) {
      case SignalType.News:
        return <NewsSignal key={index} signal={signal} />;
      case SignalType.Technology:
        return <TechnologySignal key={index} signal={signal} company={company} />;
      case SignalType.Traffic:
        return <TrafficSignal key={index} signal={signal} company={company} />;
      case SignalType.AdNetwork:
        return <AdNetworkSignal key={index} signal={signal} company={company} />;
      case SignalType.Intent:
        return <IntentSignal key={index} signal={signal} company={company} />;
      default:
        return null;
    }
  };

  const intentBannerElement = (
    <GrandAccessBanner
      key="intent-banner"
      description={t('company.signals.intent.banner.description')}
      iconImagePath={ICON_IMAGE_PATH}
      button={
        <IconButton variant={'trial'} onClick={() => setIsHookModalOpen(true)} iconName={'sparkles'}>
          {t('common.speak_with_us')}
        </IconButton>
      }
    />
  );

  const renderSignalItems = () => {
    const signalItems = data.map((signal, index) => renderSignals(signal, index));

    if (!isShowIntentBanner) {
      return signalItems;
    }

    const insertIndex = Math.min(DEFAULT_BANNER_POSITION, signalItems.length);

    return [...signalItems.slice(0, insertIndex), intentBannerElement, ...signalItems.slice(insertIndex)];
  };

  return (
    <StyledSignalsContent>
      {renderSignalItems()}
      <StyledAllSignalsButton variant="flat" onClick={handleAllSignalsClick}>
        {t('company.signals.button.all')}
        <SWReactIcons iconName="arrow-right" size="xs" iconColor={colorsPalettes.blue[500]} />
      </StyledAllSignalsButton>
      <Modal isOpen={isHookModalOpen} onClose={() => setIsHookModalOpen(false)}>
        <ContactUsSuccessModal />
      </Modal>
    </StyledSignalsContent>
  );
};
